import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Save,
  Plus,
  Rocket,
  Globe,
  ExternalLink,
  ChevronDown,
  History,
  BarChart3,
  Settings,
  Pencil,
  Eye,
  EyeOff,
  Undo2,
  AlertTriangle,
  Palette,
  Network,
  Code2,
  Link as LinkIcon,
} from 'lucide-react';
import { cn } from "@/lib/utils";
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/BackButton';
import { Separator } from '@/components/ui/separator';
import { useAccountManager } from '@/hooks/use-account-manager';
import { useValidateFunnel } from '@/lib/api/funnels';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Storefront } from '../types';
import { generateSlug } from '../utils/helpers';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useStore } from '@/lib/api/stores';
import { PromoteToProductionDialog } from './PromoteToProductionDialog';
import { DemoteToStagingDialog } from './DemoteToStagingDialog';
import { FunnelVersionHistoryDialog } from './FunnelVersionHistoryDialog';
import { QuickStepSelectionDialog } from './QuickStepSelectionDialog';
import { StepType } from '../types';
import type { IntegrationProvider } from '@/lib/integrations-constants';
import type { StepMissingDesign } from '../hooks/useFunnelSave';

interface FunnelHeaderProps {
  storeId: string;
  funnel: Storefront;
  onFunnelChange: (funnel: Storefront) => void;
  hasUnsavedChanges: boolean;
  // Returns whether the save succeeded (false = validation/mutation failure);
  // void-returning callers are treated as success for backwards compatibility.
  onSave: () => void | Promise<boolean | void>;
  onDiscard: () => void;
  isSaving: boolean;
  viewMode: 'visual' | 'flow' | 'json';
  onViewModeChange: (mode: 'visual' | 'flow' | 'json') => void;
  onAddStep?: (insertAfterCol?: number, stepType?: StepType) => void;
  onAddAlternative?: () => void;
  selectedStepId?: string | null;
  urlViewMode: 'staging' | 'production';
  onUrlViewModeChange: (mode: 'staging' | 'production') => void;
  showPreviews?: boolean;
  onShowPreviewsChange?: (show: boolean) => void;
  stepsMissingDesign?: StepMissingDesign[];
  onSelectStep?: (stepId: string) => void;
  showAnalytics?: boolean;
  onShowAnalyticsChange?: (show: boolean) => void;
  onClearDraft?: () => void;
  onCreateDirectLink?: () => void;
  /** Set when no checkout step can be linked. Disables the action and is shown
   *  as the tooltip, so the merchant learns why instead of finding a dead button. */
  directLinkDisabledReason?: string;
}

export function FunnelHeader({
  storeId,
  funnel,
  onFunnelChange,
  hasUnsavedChanges,
  onSave,
  onDiscard,
  isSaving,
  viewMode,
  onViewModeChange,
  onAddStep,
  selectedStepId,
  urlViewMode,
  onUrlViewModeChange,
  showPreviews = true,
  onShowPreviewsChange,
  stepsMissingDesign = [],
  onSelectStep,
  showAnalytics = false,
  onShowAnalyticsChange,
  onClearDraft,
  onCreateDirectLink,
  directLinkDisabledReason,
}: FunnelHeaderProps) {
  const { t } = useTranslation('storefront');
  const { storeRoutes } = useStoreNavigate();
  const { isAccountManager, isLoading: isLoadingAccountManager } = useAccountManager();
  const { data: store } = useStore(storeId);
  const [isEditingName, setIsEditingName] = useState(false);
  // Local draft (FE-10): committing per keystroke rebuilt the whole funnel
  // object (+ slug regen + validate query + canvas fingerprint) per char.
  // Committed once on blur/Enter; Escape discards.
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [showDemoteDialog, setShowDemoteDialog] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showStepSelectionDialog, setShowStepSelectionDialog] = useState(false);

  const customDomains = funnel.productionDomains || [];
  const isPublished = customDomains.length > 0;
  const lastPromoted = funnel.lastPromotedAt ? new Date(funnel.lastPromotedAt) : null;
  const lastUpdated = funnel.updatedAt ? new Date(funnel.updatedAt) : null;
  const hasUnpublishedChanges =
    isPublished && lastPromoted && lastUpdated && lastUpdated.getTime() > lastPromoted.getTime() + 1000;

  const isLiveView = urlViewMode === 'production';
  const hasMissingDesigns = stepsMissingDesign.length > 0;

  // Reactive blocking-validation so the Save / Go-Live button reflects funnel
  // errors (duplicate path, missing entry step, incomplete config) — not just
  // missing designs. validateFunnel can't run in the CRM bundle, so use the
  // funnels.validate query; debounce the config so rapid edits don't spam it.
  const validationConfig = useMemo(
    () => ({
      id: funnel.slug,
      name: funnel.name,
      version: '1.0.0',
      nodes: funnel.nodes ?? [],
      edges: funnel.edges ?? [],
      metadata: {},
    }),
    [funnel.slug, funnel.name, funnel.nodes, funnel.edges],
  );
  const [debouncedConfig, setDebouncedConfig] = useState(validationConfig);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedConfig(validationConfig), 400);
    return () => clearTimeout(t);
  }, [validationConfig]);
  const { data: funnelValidation } = useValidateFunnel(debouncedConfig);
  const blockingIssues = (
    (funnelValidation?.issues as Array<{ type: string; message: string; stepId?: string }> | undefined) ?? []
  ).filter((i) => i.type === 'error');
  const hasBlockingIssues = blockingIssues.length > 0;
  // Save is blocked either by a missing design or a structural validation error.
  const saveBlocked = hasMissingDesigns || hasBlockingIssues;

  return (
    <div className="sticky top-0 z-50 flex-shrink-0">
      {/* Dialogs */}
      <PromoteToProductionDialog
        funnel={funnel}
        storeId={storeId}
        open={showPromoteDialog}
        onOpenChange={setShowPromoteDialog}
        onSuccess={() => {
          onClearDraft?.();
          onUrlViewModeChange('production');
        }}
      />
      <DemoteToStagingDialog
        funnel={funnel}
        storeId={storeId}
        open={showDemoteDialog}
        onOpenChange={setShowDemoteDialog}
      />
      <FunnelVersionHistoryDialog
        funnelId={funnel.id}
        environment={urlViewMode}
        open={showVersionHistory}
        onOpenChange={setShowVersionHistory}
      />
      <QuickStepSelectionDialog
        open={showStepSelectionDialog}
        onOpenChange={setShowStepSelectionDialog}
        storeType={store?.type as IntegrationProvider}
        onSelectStepType={(type) => onAddStep?.(undefined, type)}
      />

      {/* Main Header Bar */}
      <div className="border-b border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="relative flex h-12 items-center gap-2 px-3">
          {/* ── LEFT: Back + Name + Add Step ────────────────── */}
          <BackButton to={storeRoutes.STOREFRONT()} />

          <Separator orientation="vertical" className="h-6" />

          {isEditingName ? (
            <input
              value={nameDraft ?? funnel.name}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={() => {
                if (nameDraft !== null && nameDraft !== funnel.name) {
                  onFunnelChange({ ...funnel, name: nameDraft, slug: generateSlug(nameDraft) });
                }
                setNameDraft(null);
                setIsEditingName(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') { setNameDraft(null); setIsEditingName(false); }
              }}
              className="border-border/50 focus:border-border h-7 w-56 rounded-md border bg-transparent px-2 text-sm font-semibold outline-none transition-colors"
              autoFocus
            />
          ) : (
            <button
              onClick={() => setIsEditingName(true)}
              className="max-w-[200px] truncate rounded px-1.5 py-0.5 text-sm font-semibold transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              title={t('funnelHeader.clickToEditName')}
            >
              {funnel.name}
            </button>
          )}

          {viewMode === 'flow' && onAddStep && !isLiveView && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowStepSelectionDialog(true)}
              className="h-7 gap-1 px-2.5 text-xs"
            >
              <Plus className="h-3 w-3" />
              {t('funnelHeader.addStep')}
            </Button>
          )}

          {/* ── CENTER: View Switcher (Account Managers Only) ── */}
          {!isLoadingAccountManager && isAccountManager && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <ToggleGroup
                type="single"
                value={viewMode}
                onValueChange={(val) => val && onViewModeChange(val as any)}
                className="h-7 rounded-md border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-900"
              >
                <ToggleGroupItem
                  value="flow"
                  aria-label={t('funnelHeader.flowView')}
                  size="sm"
                  className="data-[state=on]:bg-primary/10 data-[state=on]:text-primary h-6 px-2.5 text-[11px] font-medium"
                >
                  <Network className="mr-1 h-3 w-3" />
                  {t('funnelHeader.flow')}
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="json"
                  aria-label={t('funnelHeader.jsonView')}
                  size="sm"
                  className="data-[state=on]:bg-primary/10 data-[state=on]:text-primary h-6 px-2.5 text-[11px] font-medium"
                >
                  <Code2 className="mr-1 h-3 w-3" />
                  {t('funnelHeader.json')}
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}

          {/* ── RIGHT: Actions ─────────────────────────────── */}
          <div className="ml-auto flex items-center gap-1.5">
            {/* Analytics toggle (date range + granularity live inside the panel) */}
            {onShowAnalyticsChange && (
              <div className="flex items-center gap-1">
                <Button
                  variant={showAnalytics ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => onShowAnalyticsChange(!showAnalytics)}
                  className={cn(
                    "h-7 gap-1 px-2 text-[11px] font-medium",
                    showAnalytics
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "text-slate-500 hover:text-slate-700",
                  )}
                >
                  <BarChart3 className="h-3 w-3" />
                  {!showAnalytics && t('funnelHeader.analytics')}
                </Button>
              </div>
            )}

            {/* ── Direct link ──────────────────────────────── */}
            {onCreateDirectLink && (
              <Tooltip>
                {/* Span trigger, not the Button: the Button base styles set
                    `disabled:pointer-events-none`, so a tooltip anchored on the
                    disabled button never opens — which would hide the one
                    message explaining why the action is unavailable. */}
                <TooltipTrigger asChild>
                  <span className="inline-flex" tabIndex={directLinkDisabledReason ? 0 : undefined}>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!!directLinkDisabledReason}
                      onClick={onCreateDirectLink}
                      className="h-7 gap-1 px-2 text-[11px] font-medium text-slate-500 hover:text-slate-700"
                    >
                      <LinkIcon className="h-3 w-3" />
                      {t('funnelHeader.directLink')}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                  {directLinkDisabledReason || t('funnelHeader.directLinkHint')}
                </TooltipContent>
              </Tooltip>
            )}

            <Separator orientation="vertical" className="h-5" />

            {/* Editor / Live toggle — only when published */}
            {isPublished && (
              <div className="flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-900">
                <button
                  onClick={() => onUrlViewModeChange('staging')}
                  className={cn(
                    'flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium transition-all',
                    !isLiveView
                      ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-800 dark:text-slate-100'
                      : 'text-slate-400 hover:text-slate-600 dark:text-slate-500',
                  )}
                >
                  <Pencil className="h-2.5 w-2.5" />
                  {t('funnelHeader.editor')}
                </button>
                <button
                  onClick={() => onUrlViewModeChange('production')}
                  className={cn(
                    'flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium transition-all',
                    isLiveView
                      ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-800 dark:text-slate-100'
                      : 'text-slate-400 hover:text-slate-600 dark:text-slate-500',
                  )}
                >
                  <Globe className="h-2.5 w-2.5" />
                  {t('funnelHeader.live')}
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                  </span>
                </button>
              </div>
            )}

            {/* ── Save (with discard in dropdown) ──────────── */}
            <div className="flex items-center">
              <Tooltip>
                {/* The trigger is a span, not the button itself: the Button
                    base styles apply `disabled:pointer-events-none`, so when
                    save is blocked the disabled button emits no pointer events
                    and a tooltip anchored on it can never open — hiding the
                    issue list exactly when the user needs it to un-block. The
                    span keeps receiving hover/focus regardless. */}
                <TooltipTrigger asChild>
                  <span className="inline-flex" tabIndex={saveBlocked ? 0 : undefined}>
                  <Button
                    size="sm"
                    onClick={onSave}
                    disabled={isSaving || saveBlocked}
                    variant={hasUnsavedChanges ? 'default' : 'outline'}
                    className={cn(
                      'h-7 gap-1 px-2.5 text-[11px] font-medium',
                      hasUnsavedChanges && 'rounded-r-none',
                      saveBlocked && 'cursor-not-allowed opacity-50',
                    )}
                    data-tour="save-button"
                  >
                    {hasMissingDesigns ? (
                      <>
                        <AlertTriangle className="h-3 w-3 text-amber-500" />
                        {t('funnelHeader.missingCount', { count: stepsMissingDesign.length })}
                      </>
                    ) : hasBlockingIssues ? (
                      <>
                        <AlertTriangle className="h-3 w-3 text-red-500" />
                        {blockingIssues.length} {blockingIssues.length === 1 ? 'issue' : 'issues'}
                      </>
                    ) : isSaving ? (
                      t('funnelHeader.saving')
                    ) : hasUnsavedChanges ? (
                      <>
                        <Save className="h-3 w-3" />
                        {t('funnelHeader.save')}
                      </>
                    ) : (
                      <span className="text-slate-500">{t('funnelHeader.saved')}</span>
                    )}
                  </Button>
                  </span>
                </TooltipTrigger>
                {hasMissingDesigns ? (
                  <TooltipContent side="bottom" className="max-w-[220px]">
                    <p className="text-xs">
                      {t('funnelHeader.missingDesignTooltip', { count: stepsMissingDesign.length })}
                    </p>
                    <div className="mt-1 space-y-0.5">
                      {stepsMissingDesign.slice(0, 3).map((s) => (
                        <button
                          key={s.variantId ? `${s.id}-${s.variantId}` : s.id}
                          onClick={() => onSelectStep?.(s.id)}
                          className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[10px] hover:bg-slate-100"
                        >
                          <Palette className="h-2.5 w-2.5 text-amber-500" />
                          <span className="truncate">{s.name}</span>
                        </button>
                      ))}
                    </div>
                  </TooltipContent>
                ) : hasBlockingIssues ? (
                  <TooltipContent side="bottom" className="max-w-[280px]">
                    <p className="text-xs font-medium">Fix before saving:</p>
                    <div className="mt-1 space-y-0.5">
                      {blockingIssues.slice(0, 4).map((issue, idx) => (
                        <button
                          key={idx}
                          onClick={() => issue.stepId && onSelectStep?.(issue.stepId)}
                          disabled={!issue.stepId}
                          className={cn(
                            'flex w-full items-start gap-1 rounded px-1 py-0.5 text-left text-[10px]',
                            issue.stepId && 'hover:bg-slate-100',
                          )}
                        >
                          <AlertTriangle className="mt-0.5 h-2.5 w-2.5 shrink-0 text-red-500" />
                          <span>{issue.message}</span>
                        </button>
                      ))}
                    </div>
                  </TooltipContent>
                ) : null}
              </Tooltip>

              {/* Discard dropdown — visible whenever there are unsaved
                  changes, INCLUDING when save is blocked by a validation
                  issue: discarding is the escape hatch out of a blocked
                  edit, so gating it on !saveBlocked left users fully stuck
                  (can't save, can't revert). */}
              {hasUnsavedChanges && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant={hasUnsavedChanges ? 'default' : 'outline'}
                      className="h-7 rounded-l-none border-l border-l-white/20 px-1"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem onClick={onDiscard} className="text-xs text-red-600 focus:text-red-600">
                      <Undo2 className="mr-2 h-3.5 w-3.5" />
                      {t('funnelHeader.discardChanges')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* ── Publish / Go Live ────────────────────────── */}
            {isPublished && hasUnpublishedChanges && !isLiveView && (
              <Button
                size="sm"
                onClick={async () => {
                  if (hasUnsavedChanges) {
                    // Await the save and only open the promote dialog on success —
                    // the previous fixed 500ms timer raced the save (routing swaps
                    // take seconds) and opened the dialog even when validation
                    // failed, letting users promote a stale config to production.
                    const saved = await onSave();
                    if (saved === false) return;
                  }
                  setShowPromoteDialog(true);
                }}
                disabled={isSaving || saveBlocked}
                className="h-7 gap-1 bg-emerald-600 px-2.5 text-[11px] font-medium text-white hover:bg-emerald-700"
              >
                <Rocket className="h-3 w-3" />
                {t('funnelHeader.publish')}
              </Button>
            )}
            {!isPublished && (
              <Tooltip>
                {/* Span trigger for the same reason as the save button:
                    the disabled button swallows pointer events, which would
                    keep this tooltip from ever opening. */}
                <TooltipTrigger asChild>
                  <span className="inline-flex" tabIndex={saveBlocked || hasUnsavedChanges ? 0 : undefined}>
                  <Button
                    size="sm"
                    onClick={() => setShowPromoteDialog(true)}
                    disabled={saveBlocked || hasUnsavedChanges}
                    className="h-7 gap-1 bg-emerald-600 px-2.5 text-[11px] font-medium text-white hover:bg-emerald-700"
                  >
                    <Rocket className="h-3 w-3" />
                    {t('funnelHeader.goLive')}
                  </Button>
                  </span>
                </TooltipTrigger>
                {hasUnsavedChanges && (
                  <TooltipContent side="bottom">
                    <p className="text-xs">{t('funnelHeader.saveFirstBeforeLive')}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            )}

            {/* ── Settings overflow ────────────────────────── */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600 dark:text-slate-500">
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {/* Previews toggle */}
                {onShowPreviewsChange && viewMode === 'flow' && (
                  <>
                    <DropdownMenuItem onClick={() => onShowPreviewsChange(!showPreviews)} className="text-xs">
                      {showPreviews ? <EyeOff className="mr-2 h-3.5 w-3.5" /> : <Eye className="mr-2 h-3.5 w-3.5" />}
                      {showPreviews ? t('funnelHeader.hidePreviews') : t('funnelHeader.showPreviews')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}

                {/* Slug */}
                <DropdownMenuLabel className="text-[10px] font-normal uppercase tracking-wider text-muted-foreground">
                  {t('funnelHeader.urlSlug')}
                </DropdownMenuLabel>
                <div className="px-2 pb-2">
                  <input
                    value={funnel.slug}
                    onChange={(e) => onFunnelChange({ ...funnel, slug: generateSlug(e.target.value) })}
                    className="border-border/50 hover:border-border focus:border-border h-7 w-full rounded-md border bg-transparent px-2 font-mono text-[11px] outline-none transition-colors"
                    placeholder={t('funnelHeader.slugPlaceholder')}
                  />
                </div>

                {/* Published-only options */}
                {isPublished && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] font-normal uppercase tracking-wider text-muted-foreground">
                      {t('funnelHeader.liveDomains')}
                    </DropdownMenuLabel>
                    {customDomains.map((domain) => (
                      <DropdownMenuItem key={domain} asChild>
                        <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer" className="flex cursor-pointer items-center justify-between text-xs">
                          <span className="truncate">{domain}</span>
                          <ExternalLink className="h-3 w-3 opacity-40" />
                        </a>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-xs" onClick={() => setShowVersionHistory(true)}>
                      <History className="mr-2 h-3.5 w-3.5" />
                      {t('funnelHeader.versionHistory')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-xs text-red-600 focus:bg-red-50 focus:text-red-600"
                      onClick={() => setShowDemoteDialog(true)}
                    >
                      <Globe className="mr-2 h-3.5 w-3.5" />
                      {t('funnelHeader.unpublish')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

    </div>
  );
}
